import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, Award } from "lucide-react";
import { Logo } from "@/components/Logo";

/**
 * PaymentSuccess — clean confirmation. If the completed purchase was for a
 * founding lookup key, we surface the assigned seat number ("You are founding
 * member #138") so the user immediately feels the permanent status.
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [status, setStatus] = useState("pending");
  const [meta, setMeta] = useState({});

  useEffect(() => {
    if (!sid) return;
    let polls = 0;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (!cancelled) setMeta(r.data);
        if (r.data.payment_status === "paid") { setStatus("paid"); return; }
      } catch (err) { console.warn("Payment status poll failed:", err); }
      polls++;
      if (polls < 15 && !cancelled) setTimeout(poll, 2000);
      else if (!cancelled) setStatus("timeout");
    };
    poll();
    return () => { cancelled = true; };
  }, [sid]);

  const isFounding = meta?.lookup_key === "founding_member_lifetime";
  const foundingNumber = meta?.founding_number;

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <div className="text-center max-w-[520px]">
        <Logo size={40}/>

        {status === "pending" && (
          <>
            <Loader2 size={28} className="mx-auto mt-8 mb-4 animate-spin text-[#F97316]"/>
            <h1 className="wf-h1 mb-2">Confirming your payment…</h1>
            <p className="wf-body text-[#6B7280]">This usually takes just a few seconds.</p>
          </>
        )}

        {status === "paid" && (
          <>
            <CheckCircle2 size={40} className="mx-auto mt-8 mb-4 text-[#F97316]"/>
            {isFounding && foundingNumber ? (
              <>
                <div className="wf-eyebrow mb-3">Founding Member</div>
                <h1 className="wf-h1 mb-3">
                  You are founding member <span className="tabular-nums">#{foundingNumber}</span>.
                </h1>
                <p className="wf-body text-[#6B7280] mb-6">
                  Your badge and name are now permanent on the directory. Your
                  price is locked for life. Welcome to the origin story.
                </p>
                <div
                  className="inline-flex items-center gap-2 h-9 px-3 rounded-full bg-[#0B1220] text-white text-[13px] font-medium mb-6"
                  data-testid="founding-badge"
                >
                  <Award size={13}/> Founding Member #{foundingNumber}
                </div>
              </>
            ) : (
              <>
                <h1 className="wf-h1 mb-3">You're in.</h1>
                <p className="wf-body text-[#6B7280] mb-6">
                  Welcome to Wikificial. Your benefits are active immediately.
                </p>
              </>
            )}
            <div className="flex items-center justify-center gap-3">
              <Link to="/dashboard" className="btn-coral" data-testid="success-go-dashboard">
                Go to dashboard
              </Link>
              <Link to="/" className="btn-ghost" data-testid="success-explore">
                Explore the directory
              </Link>
            </div>
          </>
        )}

        {status === "timeout" && (
          <>
            <h1 className="wf-h1 mt-8 mb-2">Still processing…</h1>
            <p className="wf-body text-[#6B7280]">This usually resolves in seconds. Refresh in a moment.</p>
          </>
        )}
      </div>
    </div>
  );
}
